import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, ExternalLink, Loader2, ShieldQuestion } from 'lucide-react'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import apiClient from '@/api/client'
import { useToast } from '@/hooks/use-toast'
import { formatDateTimeHuman } from '@/lib/utils'
import type { PRAUnresolvedClaim } from '@/types'
import { getStoredUserRole } from '@/lib/staff-roles'
import { praClaimActionsFor } from '@/lib/praClaimActions'

/**
 * PRA reconciliation worklist.
 *
 * PRA is not idempotent — the same USIN posted three times returns three
 * distinct invoice numbers — and it publishes no query-by-USIN endpoint. So
 * when a submission fails without a readable response (timeout, reset, a
 * process killed mid-POST), the system genuinely cannot tell whether PRA filed
 * the invoice.
 *
 * Rather than guess — one way double-files a tax record, the other leaves a
 * sale unreported — the submission path parks the claim and stops. This is
 * where those orders surface, and the only place a human can close them.
 *
 * The card hides itself when there is nothing to do, so it costs nothing on a
 * healthy tenant.
 */
export function PraReconciliationCard() {
  const { toast } = useToast()
  const queryClient = useQueryClient()
  // Who may act (F-CTRL-N66): admin declares "found" or resubmits; the
  // manager may resubmit — after checking the portal exactly as an admin
  // would — but never declare a claim found; accountant reads only. The
  // card hides what a role cannot do rather than let it hit a 403.
  const viewerRole = getStoredUserRole()
  const actions = praClaimActionsFor(viewerRole)

  const [invoiceDraft, setInvoiceDraft] = useState<Record<string, string>>({})
  const [busyId, setBusyId] = useState<string | null>(null)

  const query = useQuery({
    queryKey: ['fiscal', 'pra', 'unresolved'],
    queryFn: () => apiClient.getPRAUnresolved(),
    staleTime: 15_000,
    refetchInterval: 60_000,
  })

  const claims: PRAUnresolvedClaim[] = query.data?.data ?? []

  const resolve = useMutation({
    mutationFn: (vars: { id: string; action: 'found' | 'absent'; invoice?: string }) =>
      // "absent" rides the fiscalRetry route (admin + manager); "found" the
      // admin-only resolve route. Both roles use the same resubmit path so a
      // manager never has to name an action the backend would refuse.
      vars.action === 'absent'
        ? apiClient.resubmitPRAClaim(vars.id)
        : apiClient.resolvePRAClaim(vars.id, {
            action: vars.action,
            invoice_number: vars.invoice,
          }),
    onSuccess: (res, vars) => {
      if (vars.action === 'found') {
        toast({ title: 'Recorded as filed', description: res.message || undefined })
      } else {
        // F-FBR-N5: the server now says whether a resubmission was actually
        // queued (and whether this tenant runs the worker that drains the
        // queue). Before this the card said "Released for resubmission" on
        // every click while the enqueue had silently failed (F-FBR-N4).
        const requeued = res.data?.requeued === true
        const workerEnabled = res.data?.worker_enabled !== false
        toast({
          title: requeued
            ? workerEnabled
              ? 'Released — resubmission queued'
              : 'Released — queued, but the fiscal worker is off on this server'
            : 'Released — NOT re-queued',
          description: res.message || undefined,
          variant: requeued && workerEnabled ? 'default' : 'destructive',
        })
      }
      queryClient.invalidateQueries({ queryKey: ['fiscal', 'pra', 'unresolved'] })
      queryClient.invalidateQueries({ queryKey: ['fiscal-jobs'] })
    },
    onError: (e: Error) =>
      toast({ title: 'Could not resolve — nothing was changed', description: e.message, variant: 'destructive' }),
    onSettled: () => setBusyId(null),
  })

  // Nothing parked: stay out of the way entirely.
  if (!query.isLoading && claims.length === 0) return null

  return (
    <Card className="border-amber-300 dark:border-amber-900/60">
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2 text-base">
              <ShieldQuestion className="w-4 h-4 text-amber-600" />
              PRA reconciliation
              {claims.length > 0 && (
                <Badge variant="outline" className="border-amber-300 text-amber-800 dark:text-amber-200">
                  {claims.length}
                </Badge>
              )}
            </CardTitle>
            <CardDescription>
              These orders were sent to PRA but never got a readable reply, so we cannot tell whether
              PRA filed them. They are <strong>not</strong> retried automatically — PRA issues a new
              invoice number on every resend, so a blind retry would file the sale twice.
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {query.isLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : (
          <>
            <div className="rounded-md border border-amber-300/60 bg-amber-50/60 px-3 py-2 text-xs dark:bg-amber-950/30 dark:border-amber-900/40">
              <p className="flex items-start gap-2">
                <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0 text-amber-600" />
                <span>
                  Look each USIN up in the PRA portal (IMS Fiscal Report), then say what is actually
                  there. Only choose <strong>Not in portal</strong> if you have checked and it is
                  genuinely absent — that is what allows a resubmission.
                </span>
              </p>
            </div>

            {claims.map((c) => {
              const draft = invoiceDraft[c.id] ?? ''
              const busy = busyId === c.id && resolve.isPending
              return (
                <div key={c.id} className="rounded-md border p-3 space-y-3">
                  <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4 text-xs">
                    <div>
                      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Order</div>
                      <div className="font-mono">{c.order_number || c.order_id?.slice(0, 8) || '—'}</div>
                    </div>
                    <div className="lg:col-span-2">
                      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
                        USIN — search this in the PRA portal
                      </div>
                      <div className="font-mono break-all font-semibold">{c.usin}</div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Stuck since</div>
                      <div>{formatDateTimeHuman(c.claimed_at)}</div>
                    </div>
                  </div>

                  <div className="text-[11px] text-muted-foreground">
                    POS ID {c.posid} · attempt {c.attempt_count} · {c.workflow}
                    {c.last_error ? <span className="block mt-1 break-words">{c.last_error}</span> : null}
                  </div>

                  {(actions.canDeclareFound || actions.canResubmit) && (
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                      {actions.canDeclareFound && (
                        <Input
                          value={draft}
                          onChange={(e) => setInvoiceDraft((s) => ({ ...s, [c.id]: e.target.value }))}
                          placeholder="PRA invoice number from the portal"
                          className="h-8 text-xs font-mono"
                        />
                      )}
                      <div className="flex gap-2 shrink-0">
                        {actions.canDeclareFound && (
                          <Button
                            type="button"
                            size="sm"
                            disabled={!draft.trim() || busy}
                            onClick={() => {
                              setBusyId(c.id)
                              resolve.mutate({ id: c.id, action: 'found', invoice: draft.trim() })
                            }}
                          >
                            {busy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : null}
                            Found in portal
                          </Button>
                        )}
                        {actions.canResubmit && (
                          <Button
                            type="button"
                            size="sm"
                            variant={actions.canDeclareFound ? 'outline' : 'default'}
                            disabled={busy}
                            onClick={() => {
                              if (
                                !window.confirm(
                                  `Confirm this USIN is NOT in the PRA portal:\n\n${c.usin}\n\n` +
                                    'The order will be submitted again. If PRA does hold it, this files the sale twice.',
                                )
                              )
                                return
                              setBusyId(c.id)
                              resolve.mutate({ id: c.id, action: 'absent' })
                            }}
                          >
                            {busy && !actions.canDeclareFound ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : null}
                            Not in portal — resubmit
                          </Button>
                        )}
                      </div>
                    </div>
                  )}

                  <a
                    className="inline-flex items-center gap-1 text-[11px] text-muted-foreground underline"
                    href="https://reg.pra.punjab.gov.pk/"
                    target="_blank"
                    rel="noreferrer noopener"
                  >
                    Open the PRA portal <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              )
            })}
          </>
        )}
      </CardContent>
    </Card>
  )
}
